import { useMemo } from "react";

import { useAuth } from "../AuthContext";
import { useUserValues } from "../userprofile/UserContext";
import { hasPlussAccess, normalizePlanName } from "../utils/planUtils";

const VIEW_FASIT = "view_fasit";
const VIEW_DETAILED_SOLUTION = "view_detailed_solution";
const ACCESS_PREMIUM_PROGRESS_HISTORY = "access_premium_progress_history";
const ACCESS_AI_CHAT = "access_ai_chat";

export default function usePlanAccess() {
  const { isAuthenticated } = useAuth() || {};
  const { userData } = useUserValues() || {};
  const rawPlan = userData?.plan;
  const normalizedPlan = rawPlan ? normalizePlanName(rawPlan) : null;
  const planKnown = !isAuthenticated || Boolean(userData);
  const entitlementList = Array.isArray(userData?.entitlements)
    ? userData.entitlements
    : null;
  const entitlementSet = useMemo(
    () => new Set(entitlementList || []),
    [entitlementList],
  );
  const entitlementsKnown = entitlementList !== null;
  // Treat as free only after plan is known (or unauthenticated).
  const isFree =
    !isAuthenticated ||
    (planKnown && (!normalizedPlan || normalizedPlan === "free"));
  // Current profiles return capabilities from the server. The plan fallback
  // only keeps older/local fixtures usable while they migrate; AI never gets a
  // fallback because it must fail closed.
  const legacyPaidFallback =
    Boolean(isAuthenticated) && hasPlussAccess(rawPlan);
  // Progress tracking is free for signed-in learners; the name predates that.
  const hasPluss =
    Boolean(isAuthenticated) &&
    (entitlementsKnown
      ? entitlementSet.has(ACCESS_PREMIUM_PROGRESS_HISTORY)
      : legacyPaidFallback);
  // Downloads still need a paid plan or a content manager.
  const hasPaidPlan =
    Boolean(isAuthenticated) &&
    (typeof userData?.hasPaidPlan === "boolean"
      ? userData.hasPaidPlan
      : legacyPaidFallback);
  const isPro = Boolean(isAuthenticated) && normalizedPlan === "pro";
  const canViewFasit =
    !isAuthenticated || !entitlementsKnown || entitlementSet.has(VIEW_FASIT);
  // Worked solutions are open to everyone, signed in or not.
  const canViewDetailedSolution =
    !isAuthenticated ||
    !entitlementsKnown ||
    entitlementSet.has(VIEW_DETAILED_SOLUTION);
  const canUseAiChat =
    Boolean(isAuthenticated) &&
    entitlementsKnown &&
    entitlementSet.has(ACCESS_AI_CHAT);

  return useMemo(
    () => ({
      plan: normalizedPlan || rawPlan || "",
      rawPlan: rawPlan || "",
      isAuthenticated: Boolean(isAuthenticated),
      planKnown,
      entitlementsKnown,
      entitlements: entitlementList || [],
      isFree,
      hasPluss,
      hasPaidPlan,
      isPro,
      canViewFasit,
      canViewDetailedSolution,
      canUseAiChat,
      canChangeDifficulty: true,
      canChangeProblemType: true,
      // Browsing collections is public; plan gating only applies to premium features.
      canUseCollections: true,
    }),
    [
      canUseAiChat,
      canViewDetailedSolution,
      canViewFasit,
      entitlementList,
      entitlementsKnown,
      hasPaidPlan,
      hasPluss,
      isAuthenticated,
      isFree,
      isPro,
      normalizedPlan,
      planKnown,
      rawPlan,
    ],
  );
}
